import { createRoot } from "react-dom/client"
import "@fontsource/montserrat"
import { ChakraProvider, EnvironmentProvider, Portal } from "@chakra-ui/react"
import { StrictMode, useRef, useEffect } from "react"
import createCache, { StylisPlugin } from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { BrowserRouter, MemoryRouter, useLocation, useNavigate } from "react-router-dom"
import createStylingSystem from "@hydrologiq/shared/chakra/styling/styling"
import { DashboardUser } from "@hydrologiq/common/providers/FirebaseAuth"
import { TenantProfile, TenantSectionCopy } from "@hydrologiq/gql-experience/graphql"

import { CopyProvider, DefaultSectionCopy, Section } from "@hydrologiq/shared/providers/CopyProvider"
import { FeaturesProvider, TenantFeatures } from "@hydrologiq/shared/providers/Features"
import { SectionFeaturesProvider } from "@hydrologiq/shared/providers/SectionFeatures"
import { SectionFeatures } from "@hydrologiq/shared/types/sectionFeatures"
import Auth from "@hydrologiq/shared/Auth"
import { ProfileProvider } from "./providers/ProfileProvider"
import { NavigationProvider } from "./providers/NavigationProvider"

export interface CreateSectionConfig {
  sectionName: string
  section: Section
  RoutingComponent: React.ComponentType<{
    basePath?: string
    user?: DashboardUser
    container?: React.RefObject<HTMLElement>
  }>
  defaultCopy?: DefaultSectionCopy
  defaultFeatures?: TenantFeatures
  graphqlUri: string
  restEndpoint?: string
  cacheConfig?: any
  // New: Support for dual endpoints
  primaryGraphqlUri?: string
  legacyGraphqlUri?: string
  defaultSectionFeatures?: SectionFeatures
  // New: Support for section-specific CSS
  sectionCss?: string
  profile?: TenantProfile
}

// Route change callback type
export type RouteChangeCallback = (newSubRoute: string) => void

export function createSectionFactory(config: CreateSectionConfig) {
  const {
    sectionName,
    section,
    RoutingComponent,
    defaultCopy,
    defaultFeatures,
    graphqlUri,
    restEndpoint,
    cacheConfig,
    primaryGraphqlUri,
    legacyGraphqlUri,
    defaultSectionFeatures,
    sectionCss, // New: Section-specific CSS
    profile: defaultProfile,
  } = config
  // Component to handle route change notifications for MemoryRouter
  const RouteChangeNotifier = ({ onRouteChange }: { onRouteChange?: RouteChangeCallback }) => {
    const location = useLocation()
    const prevLocationRef = useRef<string | undefined>(undefined)

    useEffect(() => {
      const currentLocation = location.pathname + location.search
      // Only call onRouteChange when location actually changes
      if (onRouteChange && prevLocationRef.current !== undefined && prevLocationRef.current !== currentLocation) {
        onRouteChange(currentLocation)
      }
      prevLocationRef.current = currentLocation
    }, [location.pathname, location.search, onRouteChange])

    return null
  }

  // Component to handle browser navigation changes and sync MemoryRouter
  const BrowserNavigationSync = ({
    onBrowserNavigationChange
  }: {
    onBrowserNavigationChange?: (callback: RouteChangeCallback) => void
  }) => {
    const navigate = useNavigate()

    // Register navigation handler with hub when component mounts
    useEffect(() => {
      if (!onBrowserNavigationChange) return

      // Create handler function that navigates MemoryRouter
      const handleBrowserNavigation = (subRoute: string) => {
        // Normalize sub-route (ensure leading slash)
        const normalizedSubRoute = subRoute.startsWith("/") ? subRoute : `/${subRoute}`
        // Navigate the MemoryRouter to the new route
        navigate(normalizedSubRoute)
      }

      // Register the handler with the hub
      onBrowserNavigationChange(handleBrowserNavigation)

      // Cleanup: unregister handler by passing null
      return () => {
        // Clear the callback in the hub by passing null
        // This ensures the hub doesn't try to call a stale callback
        onBrowserNavigationChange(() => { })
      }
    }, [navigate, onBrowserNavigationChange])

    return null
  }

  const WidgetWrapper = ({
    root,
    shadow,
    dashboard = true,
    user,
    copy,
    features,
    Router = BrowserRouter,
    token,
    authType,
    endpointOverrides,
    sectionFeatures = defaultSectionFeatures,
    sectionCss,
    profile = defaultProfile,
  }: {
    root: HTMLElement
    shadow?: ShadowRoot
    dashboard?: boolean
    user?: DashboardUser
    copy?: TenantSectionCopy
    features?: TenantFeatures
    Router?: React.ComponentType<any>
    token?: string
    authType?: "firebase" | "better-auth"
    endpointOverrides?: {
      primaryGraphqlUri?: string
      legacyGraphqlUri?: string
      restEndpoint?: string
      // Memory router specific options
      initialEntries?: string[]
      onRouteChange?: RouteChangeCallback
      navigate?: (path: string) => void
      onBrowserNavigationChange?: (callback: RouteChangeCallback) => void
    }
    sectionFeatures?: SectionFeatures
    sectionCss?: string // New: Section-specific CSS
    profile?: TenantProfile
  }) => {
    const system = createStylingSystem()

    const wrapInLayer: (layerName: string) => StylisPlugin = (layerName) => (node) => {
      if (node.parent) return
      const child = { ...node, parent: node, root: node }
      Object.assign(node, {
        children: [child],
        length: 6,
        parent: null,
        props: [layerName],
        return: "",
        root: null,
        type: "@layer",
        value: `@layer ${layerName}`,
      })
    }
    const container = shadow || root
    const cache = createCache({
      key: sectionName,
      container: container,
      stylisPlugins: [wrapInLayer(sectionName)],
      prepend: true,
      speedy: false,
    })
    cache.compat = true
    const rootRef = useRef<HTMLElement>(root)

    // Inject section-specific CSS into shadow DOM
    useEffect(() => {
      if (shadow && sectionCss) {
        const existingStyle = shadow.querySelector(`style[data-section-css="${sectionName}"]`)
        if (!existingStyle) {
          const sectionStyle = document.createElement("style")
          sectionStyle.setAttribute("data-section-css", sectionName)
          sectionStyle.textContent = sectionCss
          shadow.appendChild(sectionStyle)
        }
      }
    }, [shadow, sectionCss, sectionName])

    // Determine which GraphQL URIs to use (with overrides from hub)
    const finalGraphqlUri = endpointOverrides?.legacyGraphqlUri || legacyGraphqlUri || graphqlUri
    const finalPrimaryGraphqlUri = endpointOverrides?.primaryGraphqlUri || primaryGraphqlUri
    const finalLegacyGraphqlUri = endpointOverrides?.legacyGraphqlUri || legacyGraphqlUri || graphqlUri
    const finalRestEndpoint = endpointOverrides?.restEndpoint || restEndpoint

    // If no endpoint overrides provided (standalone mode), ensure we still have proper auth handling
    const finalToken = token
    const finalAuthType = authType || (token ? "better-auth" : undefined)

    // Extract memory router specific options
    const initialEntries = endpointOverrides?.initialEntries
    const onRouteChange = endpointOverrides?.onRouteChange
    const navigate = endpointOverrides?.navigate
    const onBrowserNavigationChange = endpointOverrides?.onBrowserNavigationChange

    // Auto-select MemoryRouter when initialEntries are provided and no explicit Router was given.
    // This ensures the section always uses its own bundled router (same React instance),
    // avoiding duplicate-React issues when loaded as a preview/prototype bundle.
    const ResolvedRouter = Router === BrowserRouter && initialEntries ? MemoryRouter : Router

    // Router props for MemoryRouter
    const routerProps = initialEntries ? { initialEntries } : {}

    return (
      <StrictMode>
        <Portal container={rootRef}>
          <CacheProvider value={cache}>
            <EnvironmentProvider value={() => shadow || document}>
              {/* No colour-mode provider here: the hub owns the Colour Mode and sets
                  `light` / `dark` on this section's shadow `#section-body`. */}
              <ChakraProvider value={system}>
                <SectionFeaturesProvider sectionFeatures={sectionFeatures}>
                  <FeaturesProvider features={features || defaultFeatures}>
                    <CopyProvider copy={copy} defaultCopy={defaultCopy} section={section}>
                      <ProfileProvider profile={profile}>
                        <ResolvedRouter {...routerProps}>
                          {onRouteChange && <RouteChangeNotifier onRouteChange={onRouteChange} />}
                          {onBrowserNavigationChange && <BrowserNavigationSync onBrowserNavigationChange={onBrowserNavigationChange} />}
                          <NavigationProvider navigate={navigate}>
                            <Auth
                              user={user}
                              token={finalToken}
                              authType={finalAuthType}
                              graphqlUri={finalGraphqlUri}
                              primaryGraphqlUri={finalPrimaryGraphqlUri}
                              legacyGraphqlUri={finalLegacyGraphqlUri}
                              restEndpoint={finalRestEndpoint ?? ""}
                              cacheConfig={cacheConfig}
                            >
                              <RoutingComponent
                                basePath={!dashboard ? "" : undefined}
                                user={user}
                                container={rootRef}
                              />
                            </Auth>
                          </NavigationProvider>
                        </ResolvedRouter>
                      </ProfileProvider>
                    </CopyProvider>
                  </FeaturesProvider>
                </SectionFeaturesProvider>
              </ChakraProvider>
            </EnvironmentProvider>
          </CacheProvider>
        </Portal>
      </StrictMode>
    )
  }

  // Return the createSection function with the expected signature
  return function createSection(
    container: HTMLElement,
    shadow?: ShadowRoot,
    dashboard?: boolean,
    user?: DashboardUser,
    copy?: TenantSectionCopy,
    Router?: React.ComponentType<any>,
    token?: string,
    authType?: "firebase" | "better-auth",
    features?: TenantFeatures,
    // Optional endpoint overrides for dual GraphQL support and memory router options
    endpointOverrides?: {
      primaryGraphqlUri?: string
      legacyGraphqlUri?: string
      restEndpoint?: string
      // Memory router specific options
      initialEntries?: string[]
      onRouteChange?: RouteChangeCallback
      navigate?: (path: string) => void
      onBrowserNavigationChange?: (callback: RouteChangeCallback) => void
    },
    sectionFeatures?: SectionFeatures,
    profile?: TenantProfile
  ) {
    return createRoot(container).render(
      <WidgetWrapper
        root={container}
        shadow={shadow}
        dashboard={dashboard}
        user={user}
        copy={copy}
        features={features}
        Router={Router}
        token={token}
        authType={authType}
        endpointOverrides={endpointOverrides}
        sectionFeatures={sectionFeatures}
        sectionCss={sectionCss}
        profile={profile}
      />
    )
  }
}
